import React, { useEffect } from "react";
import {
  Grid,
  Card,
  Button,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";

import "./ManualUpload.css";

import useManualUpload from "./useManualUpload";

import SubmitButton from "components/Button/SubmitButton";
import LoadingModal from "components/common/loading/LoadingModal";
import SearchPatient from "components/pages/manualUpload/forms/SearchPatient";
import ReportInfoInput from "components/pages/manualUpload/forms/ReportInfoInput";
import FileUpload from "components/pages/manualUpload/forms/fileUpload/FileUpload";
import PatientInfoToggle from "components/pages/manualUpload/forms/PatientInfoToggle";
import SwitchPatientTypeModal from "components/pages/manualUpload/modals/SwitchPatientTypeModal";
import { PostAdd } from "@mui/icons-material";
import InOfficeFileUploadModal from "components/pages/manualUpload/modals/InOfficeFileUploadModal";
import InOfficePatientDetailsModal from "components/pages/manualUpload/modals/InOfficePatientDetailsModal";
import BatteryInformationInput from "components/pages/manualUpload/forms/BatteryInformationInput";
import LeadInformationInput from "components/pages/manualUpload/forms/LeadInformationInput";
import OtherDeviceInformation from "components/pages/manualUpload/forms/OtherDeviceInformation";
import { isEmpty } from "lodash";
import UCHAccountModal from "components/pages/manualUpload/modals/UCHAccountModal";
import { setTitle } from "app/store/transmission/slice/transmissionSlice";
import PatientListSelection from "components/common/patientListSelection/PatientListSelection";
import ReportTemplatesInput from "components/pages/manualUpload/forms/ReportTemplatesInput";
import LoadingSpinner from "components/LoadingSpinner";
import { setShowReportTemplateUpdateAlert } from "app/store/manualUpload/slice/manualUploadSlice";
import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";
import CrossFacilityPatientWarningModal from "components/pages/manualUpload/modals/CrossFacilityPatientWarningModal";
import BillingCodeTemplateModal from "components/pages/manualUpload/modals/MissingBillingCodeTemplateModal";

const ManualUpload = (props) => {
  const {
    fields,
    devices,
    isClinicUser,
    isIDTFBilling,
    isSubmitting,
    rpmReportUrl,
    isUCHPatient,
    selectedPatient,
    reportTemplates,
    transmissionType,
    isInOfficeUpload,
    isExistingPatient,
    patientInfoToUpdate,
    isFetchingRpmPatient,
    showAddNewDeviceModal,
    showSwitchUserTypeModal,
    showMissingBillingCodeTemplateModal,
    showCrossFacilityPatientWarningModal,
    selectedCrossFacilityPatient,
    isFetchingReportTemplates,
    inOfficeUploadPatientDetails,
    showReportTemplateUpdateAlert,
    similarPatientsFromInOfficeFile,
    showSimilarPatientsFromInOfficeFile,
    showInOfficeUploadPatientFoundDetails,
    inOfficeUploadPatientInformationForManualUpload,

    isError,
    dispatch,
    onUpdate,
    resetForm,
    saveUpload,
    onFilesSelected,
    pendingAssociatedNotes,
    pendingExtractionResults,
    showExtractButton,
    canExtractReportNotes,
    extractReportNotesTooltip,
    handleExtractReportPdfData,
    handleAddNewDevice,
    resetInOfficeUpload,
    handleInOfficeUpload,
    handlePatientTypeSwitch,
    onAddInOfficeChecksClick,
    handleExistingPatientSelect,
    handleUpdatedReportTemplateFetch,
    handleInOfficePatientDetailsSelect,
    handleInOfficePatientRecordSelect,
    setShowSimilarPatientsFromInOfficeFile,
    isInofficeClinicUpload,
    isCrossFacilityClinicUpload,
    setShowCrossFacilityPatientWarningModal,
    setSelectedCrossFacilityPatient,
  } = useManualUpload();

  const deviceAnalytics =
    inOfficeUploadPatientDetails?.inOfficeData?.patientDeviceAnalytics;

  const batteryInformation = deviceAnalytics?.deviceBatteryInformation;
  const leadInformation = deviceAnalytics?.leadInformation;
  const otherImplantInformation =
    deviceAnalytics?.burden || deviceAnalytics?.bradySettings;

  useEffect(() => {
    dispatch(
      setTitle({
        title: "Upload Report",
      })
    );
  }, []);

  return (
    <>
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            boxSizing: "border-box",
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "16px",
          }}
        >
          <Button
            color="secondary"
            variant="contained"
            onClick={onAddInOfficeChecksClick}
            style={{
              display: "flex",
              alignItems: "center",
            }}
            data-action="manual-upload-in-office-note-file-button"
          >
            <PostAdd style={{ marginRight: "6px" }} />
            <span style={{ marginTop: "3px" }}>Upload In-Office Note File</span>
          </Button>
        </Grid>

        <Grid item xs={6} style={{ boxSizing: "border-box" }}>
          <FileUpload
            onUpdate={onUpdate}
            existingReportUrl={rpmReportUrl}
            onFilesSelected={onFilesSelected}
          />
        </Grid>

        <Grid item xs={6}>
          <Card style={{ padding: "10px 0 20px", marginLeft: "12px" }}>
            <div className="manual-upload-form-spacing">
              <h2
                className="mt-2"
                data-label="manual-upload-general-patient-information-header"
              >
                General Patient Information
              </h2>

              <PatientInfoToggle
                handlePatientTypeSwitch={handlePatientTypeSwitch}
                resetForm={resetForm}
                resetInOfficeUpload={resetInOfficeUpload}
                isCrossFacilityClinicUpload={isCrossFacilityClinicUpload}
              />

              {isExistingPatient && (
                <SearchPatient
                  handlePatientSelect={(newValue) => {
                    if (isCrossFacilityClinicUpload(newValue?.account)) {
                      dispatch(setShowCrossFacilityPatientWarningModal(true));
                      dispatch(setSelectedCrossFacilityPatient(newValue));
                    } else {
                      resetInOfficeUpload();
                      handleExistingPatientSelect(newValue);
                    }
                  }}
                  isInofficeClinicUpload={isInofficeClinicUpload}
                  excludePatch
                />
              )}

              <ReportInfoInput
                fields={fields}
                onUpdate={onUpdate}
                isError={isError}
                devices={devices}
                handleAddNewDevice={handleAddNewDevice}
                showAddNewDeviceModal={showAddNewDeviceModal}
                isCrossFacilityClinicUpload={isCrossFacilityClinicUpload}
                isIDTFBilling={isIDTFBilling}
              />

              {isFetchingReportTemplates && (
                <div className="d-flex flex-end mb-2">
                  <LoadingSpinner size={20} />
                </div>
              )}

              {(selectedPatient || !isExistingPatient) &&
                reportTemplates?.length > 0 && (
                  <ReportTemplatesInput
                    reportNotes={reportTemplates}
                    onUpdate={onUpdate}
                    hasEditAccess={true}
                    className="mt-3"
                    pendingAssociatedNotes={pendingAssociatedNotes}
                    pendingExtractionResults={pendingExtractionResults}
                    showExtractButton={showExtractButton}
                    canExtractReportNotes={canExtractReportNotes}
                    extractReportNotesTooltip={extractReportNotesTooltip}
                    handleExtractReportPdfData={handleExtractReportPdfData}
                  />
                )}

              {transmissionType === "inOffice" && batteryInformation && (
                <BatteryInformationInput
                  fields={fields}
                  onUpdate={onUpdate}
                  isError={isError}
                />
              )}

              {transmissionType === "inOffice" &&
                leadInformation &&
                !isEmpty(leadInformation) && (
                  <LeadInformationInput
                    fields={fields}
                    onUpdate={onUpdate}
                    isError={isError}
                  />
                )}

              {transmissionType === "inOffice" && otherImplantInformation && (
                <OtherDeviceInformation
                  fields={fields}
                  onUpdate={onUpdate}
                  isError={isError}
                />
              )}
            </div>

            <Divider
              style={{ marginTop: "20px", width: "100%", marginLeft: 0 }}
            />

            <div className="manual-upload-form-spacing">
              <div
                className="d-flex justify-between"
                style={{ padding: "16px 0" }}
              >
                <SubmitButton
                  variant="text"
                  color="primary"
                  handleClick={resetForm}
                  buttonText="Reset"
                  data-action="manual-upload-reset-button"
                />

                <div className="d-flex">
                  {transmissionType === "inOffice" &&
                    isClinicUser &&
                    (!isExistingPatient || selectedPatient) && (
                      <FormControlLabel
                        className="mr-3"
                        sx={{
                          gap: "4px",
                          display: "flex",
                          alignItems: "center",
                        }}
                        label={
                          <span
                            style={{
                              fontSize: "16px",
                              color: fields.sendToReviewTab
                                ? "#000000"
                                : "#00000099",
                            }}
                            data-label="manual-upload-send-to-review-tab-label"
                          >
                            Send to Review Tab
                          </span>
                        }
                        control={
                          <Checkbox
                            checked={fields.sendToReviewTab}
                            size="small"
                            onChange={(e, c) =>
                              onUpdate({ sendToReviewTab: c })
                            }
                            data-action="manual-upload-send-to-review-tab-checkbox"
                          />
                        }
                      />
                    )}

                  <SubmitButton
                    className="ml-2"
                    variant="contained"
                    color="secondary"
                    buttonText="Save"
                    handleClick={saveUpload}
                    isLoading={isSubmitting}
                    disabled={isFetchingReportTemplates}
                  />
                </div>
              </div>
            </div>
          </Card>
        </Grid>
      </Grid>

      {showSwitchUserTypeModal && (
        <SwitchPatientTypeModal
          handlePatientTypeSwitch={handlePatientTypeSwitch}
        />
      )}

      {isInOfficeUpload && (
        <InOfficeFileUploadModal handleInOfficeUpload={handleInOfficeUpload} />
      )}

      {showInOfficeUploadPatientFoundDetails && (
        <InOfficePatientDetailsModal
          handlePatientSelect={handleInOfficePatientDetailsSelect}
        />
      )}

      {isSubmitting && (
        <LoadingModal loadingText="Uploading Patient Report..." />
      )}

      {isFetchingRpmPatient && (
        <LoadingModal loadingText="Fetching RPM Patient" />
      )}

      {isUCHPatient && (
        <UCHAccountModal
          fields={fields}
          onUpdate={onUpdate}
          handleSubmit={saveUpload}
        />
      )}

      {showSimilarPatientsFromInOfficeFile && (
        <PatientListSelection
          successButtonText="Confirm"
          cancelButtonText="Set as new patient"
          showSearchField={false}
          searchable={true}
          excludePatch
          title="Similar Patients Found"
          subTitle="Patient with similar information already exist in the system."
          description={
            <span>
              Similar patient records were found. Select a record to auto-fill
              the patient form or click <b>Set as New Patient</b> to use the
              in-office file data.
            </span>
          }
          patientList={similarPatientsFromInOfficeFile}
          showDrawer={showSimilarPatientsFromInOfficeFile}
          handleCancel={() => {
            handleInOfficePatientRecordSelect({
              selectedPatientRecord:
                inOfficeUploadPatientInformationForManualUpload,
            });
          }}
          handleConfirm={(result) =>
            handleInOfficePatientRecordSelect(result, true)
          }
          handleClose={() => setShowSimilarPatientsFromInOfficeFile(false)}
          confirmModalText={
            <span>Are you sure you want to select this patient?</span>
          }
        />
      )}

      {showReportTemplateUpdateAlert && (
        <ConfirmModal
          confirmText="Confirm"
          title={`${
            patientInfoToUpdate.key === "manufacturer" ? "Reset" : "Reload"
          } Report Templates`}
          show={showReportTemplateUpdateAlert}
          isConfirming={isFetchingReportTemplates}
          onConfirm={handleUpdatedReportTemplateFetch}
          onCancel={() => dispatch(setShowReportTemplateUpdateAlert(false))}
          children={
            <span data-label="manual-upload-report-template-update-message">
              Changing this field will{" "}
              {patientInfoToUpdate.key === "manufacturer" ? "reset" : "reload"}{" "}
              the report templates. Do you want to proceed?
            </span>
          }
        />
      )}
      {showCrossFacilityPatientWarningModal && (
        <CrossFacilityPatientWarningModal
          handleConfirm={() => {
            resetInOfficeUpload();
            handleExistingPatientSelect(selectedCrossFacilityPatient);
            dispatch(setSelectedCrossFacilityPatient(null));
            dispatch(setShowCrossFacilityPatientWarningModal(false));
          }}
        />
      )}
      {showMissingBillingCodeTemplateModal && <BillingCodeTemplateModal />}
    </>
  );
};

export default ManualUpload;
